import { useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Star, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { ConfirmDelete } from "@/components/ConfirmDelete";
import { api, errMsg, pct, channelPct } from "@/lib/api";

const KINDS = [
  ["tax", "Imposto sobre venda"], ["card", "Taxa de cartão"], ["commission", "Comissão de marketplace/app"],
  ["platform", "Taxa de plataforma"], ["shipping", "Frete subsidiado"], ["seller", "Comissão de vendedor"], ["other", "Outra taxa"],
];
const EMPTY = { name: "", fees: [], is_default: false };

const ChannelDialog = ({ open, onOpenChange, channel, onSaved }) => {
  const [f, setF] = useState(channel ? { name: channel.name, fees: channel.fees.map((x) => ({ ...x })), is_default: !!channel.is_default } : EMPTY);
  const [busy, setBusy] = useState(false);
  const setFee = (i, patch) => setF({ ...f, fees: f.fees.map((x, j) => (j === i ? { ...x, ...patch } : x)) });
  const save = async () => {
    if (!f.name.trim()) return toast.error("Informe o nome do canal");
    if (f.fees.some((x) => !x.label.trim())) return toast.error("Toda taxa precisa de um nome");
    setBusy(true);
    try {
      const body = { ...f, fees: f.fees.map((x) => ({ label: x.label, kind: x.kind || "other", pct: Number(x.pct) || 0 })) };
      if (channel) await api.put(`/channels/${channel.id}`, body); else await api.post("/channels", body);
      toast.success(channel ? "Canal atualizado" : "Canal criado"); onSaved(); onOpenChange(false);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="channel-dialog">
        <DialogHeader><DialogTitle className="font-display">{channel ? "Editar canal" : "Novo canal de venda"}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1.5"><label className="text-sm font-medium">Nome do canal</label><Input data-testid="channel-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Ex.: iFood, Cartão, Balcão" /></div>
          <div className="space-y-2">
            <div className="flex items-center justify-between"><label className="text-sm font-medium">Custos variáveis da venda (% sobre o preço)</label><Button size="sm" variant="outline" data-testid="channel-add-fee" onClick={() => setF({ ...f, fees: [...f.fees, { label: "", kind: "other", pct: 0 }] })}><Plus className="w-3.5 h-3.5 mr-1" />Taxa</Button></div>
            {!f.fees.length && <p className="text-xs text-muted-foreground">Nenhuma taxa. Este canal não terá custos variáveis (0%).</p>}
            {f.fees.map((x, i) => (
              <div key={i} className="grid grid-cols-[1fr_auto] gap-2 rounded-xl border border-border p-2" data-testid={`channel-fee-row-${i}`}>
                <div className="space-y-2">
                  <Input data-testid={`channel-fee-label-${i}`} value={x.label} onChange={(e) => setFee(i, { label: e.target.value })} placeholder="Nome da taxa (ex.: Comissão)" className="h-8" />
                  <div className="flex gap-2">
                    <Select value={x.kind || "other"} onValueChange={(v) => setFee(i, { kind: v })}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`channel-fee-kind-${i}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{KINDS.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
                    </Select>
                    <div className="flex items-center gap-1 shrink-0"><Input data-testid={`channel-fee-pct-${i}`} type="number" min="0" max="100" step="0.01" value={x.pct} onChange={(e) => setFee(i, { pct: e.target.value })} className="w-24 h-8 num text-right" /><span className="text-sm text-muted-foreground">%</span></div>
                  </div>
                </div>
                <Button variant="ghost" size="icon" className="text-muted-foreground" data-testid={`channel-fee-remove-${i}`} onClick={() => setF({ ...f, fees: f.fees.filter((_, j) => j !== i) })}><X className="w-4 h-4" /></Button>
              </div>
            ))}
            <div className="text-sm flex justify-between rounded-lg bg-secondary/50 px-3 py-2"><span>Total de custos variáveis</span><span className="num font-bold" data-testid="channel-total-pct">{pct(channelPct({ fees: f.fees }), 2)}</span></div>
          </div>
          <div className="flex items-center justify-between"><label className="text-sm">Canal padrão (usado nos cards das fichas)</label><Switch data-testid="channel-default-switch" checked={f.is_default} onCheckedChange={(v) => setF({ ...f, is_default: v })} /></div>
          <Button className="w-full" onClick={save} disabled={busy} data-testid="channel-save">{busy ? "Salvando..." : "Salvar canal"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ChannelsPanel = ({ channels, reload }) => {
  const [dlg, setDlg] = useState({ open: false, item: null, key: 0 });
  const del = async (c) => { try { await api.delete(`/channels/${c.id}`); toast.success("Canal excluído"); reload(); } catch (e) { toast.error(errMsg(e)); } };
  return (
    <div className="space-y-3" data-testid="channels-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-semibold text-lg">Canais de venda</h2>
        <Button size="sm" onClick={() => setDlg({ open: true, item: null, key: Date.now() })} data-testid="channel-new-btn"><Plus className="w-4 h-4 mr-1" />Novo canal</Button>
      </div>
      <p className="text-xs text-muted-foreground">Cada canal tem suas próprias taxas. Elas incidem sobre o preço de venda e nunca alteram o custo da ficha técnica.</p>
      {(channels || []).map((c) => (
        <div key={c.id} className="rounded-xl border border-border p-3 flex items-start justify-between gap-2" data-testid={`channels-panel-card-${c.id}`}>
          <div className="min-w-0">
            <p className="font-semibold flex items-center gap-1.5 truncate">{c.name}{c.is_default && <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />}</p>
            <p className="text-xs text-muted-foreground">{c.fees.length ? c.fees.map((f) => `${f.label} ${pct(f.pct, 2)}`).join(" · ") : "Sem taxas (0%)"}</p>
            <p className="text-xs num mt-0.5">Total: <span className="font-semibold">{pct(channelPct(c), 2)}</span></p>
          </div>
          <div className="flex shrink-0">
            <Button variant="ghost" size="icon" data-testid={`channel-edit-${c.id}`} onClick={() => setDlg({ open: true, item: c, key: Date.now() })}><Pencil className="w-4 h-4" /></Button>
            <ConfirmDelete what={`o canal "${c.name}"`} onConfirm={() => del(c)} testId={`channel-delete-${c.id}`} />
          </div>
        </div>
      ))}
      {dlg.open && <ChannelDialog key={dlg.key} open={dlg.open} onOpenChange={(o) => setDlg({ ...dlg, open: o })} channel={dlg.item} onSaved={reload} />}
    </div>
  );
};
